import React from 'react';
import './Toast.css';

type ToastType = 'info' | 'error' | 'success';

let toastId = 0;

/** 通知の中に置く操作のボタン。押すと onClick を呼び、通知を閉じる */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastContextToast {
  id: number;
  type: ToastType;
  message: string;
  action?: ToastAction;
}

interface ToastContextState {
  toasts: ToastContextToast[];
}

type Action =
  | { type: 'addToast'; id: number; toastType: ToastType; message: string; action?: ToastAction }
  | { type: 'deleteToast'; id: number };

export function toastContextReducer(
  state: ToastContextState,
  action: Action
): ToastContextState {
  switch (action.type) {
    case 'addToast':
      return {
        toasts: [
          ...state.toasts,
          { id: action.id, type: action.toastType, message: action.message, action: action.action },
        ],
      };
    case 'deleteToast':
      return {
        toasts: state.toasts.filter((t) => t.id !== action.id),
      };
    default:
      return state;
  }
}

/** 呼び出し側が表示時間を渡さなかったときに使う、種類ごとの表示時間（ミリ秒） */
const DEFAULT_SUCCESS_TIMEOUT = 4000;
const DEFAULT_ERROR_TIMEOUT = 8000;

/**
 * トーストを自動で閉じるまでの時間を決める。
 * 呼び出し側が時間を渡したときはその値を使い、渡さなかったときは成功とエラーに既定値を使う。
 * infoは既定値を持たず、従来どおり利用者が閉じるまで表示し続ける。
 */
function resolveToastTimeout(toastType: ToastType, timeout: number | undefined): number | undefined {
  if (timeout !== undefined) {
    return timeout;
  }
  if (toastType === 'success') {
    return DEFAULT_SUCCESS_TIMEOUT;
  }
  if (toastType === 'error') {
    return DEFAULT_ERROR_TIMEOUT;
  }
  return undefined;
}

export class ToastContextClass {
  private readonly provider: () => ToastContextData;

  constructor(provider: () => ToastContextData) {
    this.provider = provider;
  }

  getToasts() {
    const { state } = this.provider();
    if (state) {
      return state.toasts;
    }

    throw new Error('ToastContext is not initialized');
  }

  showToast(message: string, toastType: ToastType, timeout?: number, action?: ToastAction): number {
    const { dispatch } = this.provider();

    if (dispatch) {
      const id = toastId++;
      dispatch({ type: 'addToast', id, toastType, message, action });

      const resolvedTimeout = resolveToastTimeout(toastType, timeout);
      if (resolvedTimeout) {
        setTimeout(() => dispatch({ type: 'deleteToast', id }), resolvedTimeout);
      }

      return id;
    }

    throw new Error('ToastContext is not initialized');
  }

  hideToast(id: number) {
    const { dispatch } = this.provider();

    if (dispatch) {
      dispatch({ type: 'deleteToast', id });
      return;
    }

    throw new Error('ToastContext is not initialized');
  }
}

export const ToastContext = React.createContext<ToastContextClass>(
  new ToastContextClass(function () {
    return {
      state: { toasts: [] },
      dispatch() {},
    };
  })
);

export interface ToastContextData {
  state: ToastContextState;
  dispatch: React.Dispatch<Action>;
}

interface ToastProps {
  toast: ToastContextToast;
  onDelete: () => void;
}

const Toast: React.FC<ToastProps> = function (props) {
  return (
    <div className={'Toast-box ' + props.toast.type}>
      {(function () {
        switch (props.toast.type) {
          case 'info':
            return <img alt="Toast Info" className="Toast-icon" src="/toast-icons/info.png" />;
          case 'success':
            return (
              <img alt="Toast Success" className="Toast-icon" src="/toast-icons/success.png" />
            );
          case 'error':
            return <img alt="Toast Error" className="Toast-icon" src="/toast-icons/error.png" />;
        }
      })()}
      <p className="Toast-message">{props.toast.message}</p>
      {props.toast.action !== undefined && (
        <button
          type="button"
          className="Toast-action"
          onClick={() => {
            props.toast.action?.onClick();
            props.onDelete();
          }}
        >
          {props.toast.action.label}
        </button>
      )}
      <button
        className="Toast-delete_icon_container"
        onClick={() => props.onDelete()}
      >
        <img alt="Delete" className="Toast-delete_icon" src="/delete.png" />
      </button>
    </div>
  );
};

interface ToastContainerProps {
  configure(data: ToastContextData): void;
}

export const ToastContainer: React.FC<ToastContainerProps> = function (props) {
  const [state, dispatch] = React.useReducer(toastContextReducer, {
    toasts: [],
  });

  props.configure({ state, dispatch });

  return (
    <div>
      <div className="ToastContainer-container">
        {state.toasts.map((toast) => (
          <Toast
            key={toast.id}
            toast={toast}
            onDelete={() => dispatch({ type: 'deleteToast', id: toast.id })}
          />
        ))}
      </div>
    </div>
  );
};
